import { computed, Ref, unref } from 'vue';
import {
  RetailerShippingFeesMessageTypeName,
  LegacyApplicableFee,
  PurchasableAggregateApplicableFee,
} from '@monolith/legacy/modules/cart-2/types';
import useCurrency from '@core/composables/use-currency';
import { Amount } from '@core/types/amount';
import { useStore } from '@core/composables/use-store';
import {
  AnkorstoreInternalPurchasableAggregateResourceOfferNameEnum,
  AnkorstoreInternalPurchasableAggregateResourceTypeEnum2,
} from '@client/api-internal/data-contracts';

const isRetailerShippingFee = (
  fee: PurchasableAggregateApplicableFee | LegacyApplicableFee
): fee is Extract<PurchasableAggregateApplicableFee | LegacyApplicableFee, { metadata: unknown }> =>
  fee.type === AnkorstoreInternalPurchasableAggregateResourceTypeEnum2.RetailerShippingFees;

const usePurchasableAggregate = (applicableFees: Ref<(PurchasableAggregateApplicableFee | LegacyApplicableFee)[]>) => {
  const retailer = useStore().getters.retailer;
  const isRetailerEligibleAksPlus =
    retailer.business?.addresses.find(
      (address) => address.type === 'shipping_address' && !['IT', 'AT'].includes(address.country?.iso_code)
    ) ?? null;
  const isRetailerEu8 = retailer.is_eu_8 as boolean;
  const { $currency } = useCurrency();

  const retailerShippingFeeMessageType = computed<RetailerShippingFeesMessageTypeName>(() => {
    if (!unref(retailerShippingFees)) {
      return null;
    }

    if (unref(isAksPlus) && unref(isBrandEligibleForAkPlusFreeShipping)) {
      return 'freeShipping';
    }
    return unref(isAboveFranco) ? 'reorder-aboveFranco' : 'reorder-belowFranco';
  });

  const shouldShowAksPlusSignupCta = computed(
    () => isRetailerEu8 && isRetailerEligibleAksPlus && !unref(isAksPlus) && unref(isBrandEligibleForAkPlusFreeShipping)
  );

  // get fees by type
  const retailerShippingFees = computed(() => unref(applicableFees)?.find(isRetailerShippingFee) ?? null);

  // shipping fees / franco

  const isAksPlus = computed(
    () => unref(retailerShippingFees)?.metadata?.offerName === AnkorstoreInternalPurchasableAggregateResourceOfferNameEnum.AnkorstorePlus
  );
  const isDiscovery = computed(() => unref(retailerShippingFees)?.metadata?.isDiscovery);

  const isBrandEligibleForAkPlusFreeShipping = computed(
    () => !unref(retailerShippingFees)?.metadata?.brandNonEligibleForAkPlusFreeShipping
  );

  const isAboveFranco = computed(() => !unref(retailerShippingFees)?.amount?.amount);

  // `amount`/`amountLeftForFreeShipping` are generated as `{ currency: string; amount: number }` (see the
  // note on PurchasableAggregate.totalAmountBeforeDiscount in types.ts) - cast to the app-wide `Amount`
  // type (`currency: Currency`), tracked as tech debt in FC-1510.
  const retailerShippingFeesAmount = computed<Amount>(() => (unref(retailerShippingFees)?.amount as Amount) ?? null);

  const francoAmount = computed<Amount>(() =>
    unref(retailerShippingFees)
      ? {
          amount: unref(retailerShippingFees).metadata.francoAmount,
          currency: $currency.value,
        }
      : null
  );

  const francoFeeAmount = computed<Amount>(() =>
    unref(retailerShippingFees)
      ? {
          amount: unref(retailerShippingFees).metadata.francoFeeAmount,
          currency: $currency.value,
        }
      : null
  );

  const francoMaxFeeAmount = computed<Amount>(() =>
    unref(retailerShippingFees)
      ? {
          amount: unref(retailerShippingFees).metadata.francoMaxFeeAmount,
          currency: $currency.value,
        }
      : null
  );

  const amountLeftForFreeShipping = computed<Amount>(
    () => (unref(retailerShippingFees)?.amountLeftForFreeShipping as Amount | null) ?? null
  );

  return {
    amountLeftForFreeShipping,
    retailerShippingFeeMessageType,
    isAksPlus,
    isBrandEligibleForAkPlusFreeShipping,
    isDiscovery,
    retailerShippingFees,
    retailerShippingFeesAmount,
    francoMaxFeeAmount,
    francoAmount,
    francoFeeAmount,
    isAboveFranco,
    shouldShowAksPlusSignupCta,
  };
};

export default usePurchasableAggregate;
